import { Stack, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { UploadedFile } from '../../hooks/file-uploads/types';
import { groupFilesByUploadDay } from '../utils/groupFilesByUploadDay';
import AddDocumentRow, { FiledInfo } from './AddDocumentRow';

type AddDocumentsListProps = {
  files: UploadedFile[];
  filedInfo: Record<string, FiledInfo>;
  rootFolderLabel: string;
  contactNameFor: (file: UploadedFile) => string | null;
  retryingFileId?: string | null;
  onDownload: (file: UploadedFile) => void;
  onDelete: (file: UploadedFile) => void;
  onUpdateDate: (file: UploadedFile, date: Date) => void;
  onProcess: (file: UploadedFile, clientId?: string) => void;
  onRetry?: (file: UploadedFile) => void;
  onFileTo: (file: UploadedFile) => void;
  onOpenFiledFolder: (info: FiledInfo) => void;
  onSetResolved: (file: UploadedFile, isResolved: boolean) => void;
  onDismissDetectedClient?: (file: UploadedFile, clientId: string) => void;
  dismissingClientId?: string | null;
};

export default function AddDocumentsList({
  files,
  filedInfo,
  rootFolderLabel,
  contactNameFor,
  retryingFileId,
  onDownload,
  onDelete,
  onUpdateDate,
  onProcess,
  onRetry,
  onFileTo,
  onOpenFiledFolder,
  onSetResolved,
  onDismissDetectedClient,
  dismissingClientId,
}: AddDocumentsListProps) {
  const groups = groupFilesByUploadDay(files);

  return (
    <Stack gap="xxl" data-testid="recently-added-documents">
      {groups.map(group => (
        <Stack key={group.key} gap="xxs">
          <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
            {group.heading}
          </Text>
          <Stack gap="xxs">
            {group.files.map(file => (
              <AddDocumentRow
                key={file.id}
                file={file}
                filedInfo={filedInfo[file.id]}
                rootFolderLabel={rootFolderLabel}
                contactName={contactNameFor(file)}
                isRetrying={retryingFileId === file.id}
                onDownload={onDownload}
                onDelete={onDelete}
                onUpdateDate={onUpdateDate}
                onProcess={onProcess}
                onRetry={onRetry}
                onFileTo={onFileTo}
                onOpenFiledFolder={onOpenFiledFolder}
                onSetResolved={onSetResolved}
                onDismissDetectedClient={onDismissDetectedClient}
                dismissingClientId={dismissingClientId}
                defaultExpanded={group.isToday}
              />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
